import {
  RestaurantSetting,
  CreateRestaurantSettingData,
  UpdateRestaurantSettingData,
  BulkRestaurantSettingData,
  RestaurantSettingFilters,
  RestaurantSettingsResponse,
  BulkRestaurantSettingsResponse,
} from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

export const restaurantSettingsAPI = {
  getSettings: async (
    filters?: RestaurantSettingFilters,
  ): Promise<RestaurantSettingsResponse> => {
    const hc = getAPIClient();
    const query = new URLSearchParams();

    if (filters) {
      if (filters.model_type) query.append("model_type", filters.model_type);
      if (filters.key) query.append("key", filters.key);
      if (filters.limit) query.append("limit", filters.limit.toString());
      if (filters.offset) query.append("offset", filters.offset.toString());
    }

    const resp = await hc.api.v2["restaurant-settings"].$get({
      query: Object.fromEntries(query),
    });

    if (resp.ok) {
      const json = await resp.json();
      return json as RestaurantSettingsResponse;
    }
    throw new Error("Failed to fetch restaurant settings");
  },

  getSetting: async (
    key: string,
    model_type = "Restaurant",
  ): Promise<RestaurantSetting> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2["restaurant-settings"][":key"].$get({
      param: { key },
      query: { model_type },
    });

    if (resp.ok) {
      const json = await resp.json();
      return json.data as RestaurantSetting;
    }
    throw new Error("Failed to fetch restaurant setting");
  },

  createSetting: async (
    data: CreateRestaurantSettingData,
  ): Promise<RestaurantSetting> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2["restaurant-settings"].$post({
      json: data,
    });

    if (resp.ok) {
      const json = await resp.json();
      return json.data as RestaurantSetting;
    }
    throw new Error("Failed to create restaurant setting");
  },

  updateSetting: async (
    key: string,
    data: UpdateRestaurantSettingData,
  ): Promise<RestaurantSetting> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2["restaurant-settings"][":key"].$put({
      param: { key },
      json: data,
    });

    if (resp.ok) {
      const json = await resp.json();
      return json.data as RestaurantSetting;
    }
    throw new Error("Failed to update restaurant setting");
  },

  bulkUpsertSettings: async (
    data: BulkRestaurantSettingData,
  ): Promise<BulkRestaurantSettingsResponse> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2["restaurant-settings"].bulk.$post({
      json: data,
    });

    if (resp.ok) {
      const json = await resp.json();
      return json as BulkRestaurantSettingsResponse;
    }
    throw new Error("Failed to bulk update restaurant settings");
  },

  deleteSetting: async (
    key: string,
    model_type = "Restaurant",
  ): Promise<{ success: boolean }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2["restaurant-settings"][":key"].$delete({
      param: { key },
      query: { model_type },
    });

    if (resp.ok) {
      return { success: true };
    }
    throw new Error("Failed to delete restaurant setting");
  },
};
